import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import theme from 'styled-theming';

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const waveMove = keyframes`
  0% {
    transform: translateX(-100%) skewX(-15deg);
  }
  100% {
    transform: translateX(100%) skewX(-15deg);
  }
`;

const defaultGradient = theme('mode', {
  light: 'linear-gradient(90deg, #e0e0e0 25%, #f5f5f5 50%, #e0e0e0 75%)',
  dark: 'linear-gradient(90deg, #2d2d35 25%, #363640 50%, #2d2d35 75%)',
});

const SkeletonBase = styled.div`
  position: relative;
  margin: ${({ marginGap }) => marginGap || '50px 0'};
  background: ${({ bgColor }) => bgColor || defaultGradient};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: ${({ radius }) => radius || '4px'};
  overflow: hidden;
  max-width: 100%;
  box-sizing: border-box;

  ${({ waveColor }) =>
    waveColor &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: -100%;
        left: -100%;
        right: -100%;
        bottom: -100%;
        background: linear-gradient(135deg, transparent 0%, ${waveColor} 50%, transparent 100%);
        animation: ${waveMove} 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
        animation-delay: ${({ index }) => (index || 0) * 0.4}s;
      }
    `}

  ${({ shape, size, width, height }) =>
    shape === 'circle'
      ? css`
          width: ${size || '50px'};
          height: ${size || '50px'};
          border-radius: 50%;
          @media (max-width: 768px) {
            width: '40px';
            height: '40px';
          }
        `
      : shape === 'square'
        ? css`
            width: ${size || '50px'};
            height: ${size || '50px'};
            @media (max-width: 768px) {
              width: '40px';
              height: '40px';
            }
          `
        : css`
            width: ${width || '100%'};
            height: ${height || '16px'};
            margin: 5px 0;
            @media (max-width: 768px) {
              width: '100%';
              height: '14px';
            }
          `}
`;

const LinesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ lineGap }) => lineGap || '30px'};
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
`;

const SquaresWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
`;

export const SkeletonLoader = ({
  shape = 'line',
  size,
  width,
  height,
  isLoading = true,
  numLines = 1,
  children,
  bgColor, // Background color
  waveColor, // Wave animation color
  index,
  lineGap = 0,
  marginGap = 0,
  testId,
}) => {
  if (!isLoading) return children;

  if (shape === 'line') {
    return (
      <LinesWrapper
        lineGap={lineGap}
        data-testid={`${testId}`}
      >
        {Array.from({ length: numLines }).map((_, i) => (
          <SkeletonBase
            key={i}
            shape='line'
            width={width}
            height={height}
            bgColor={bgColor}
            waveColor={waveColor}
            index={index || i}
          />
        ))}
      </LinesWrapper>
    );
  }

  if (shape === 'square') {
    return (
      <SquaresWrapper data-testid={`${testId}`}>
        {Array.from({ length: numLines }).map((_, i) => (
          <SkeletonBase
            key={i}
            shape='square'
            size={size}
            bgColor={bgColor}
            waveColor={waveColor}
            index={index || i}
            marginGap={marginGap}
          />
        ))}
      </SquaresWrapper>
    );
  }

  return (
    <SkeletonBase
      data-testid={testId}
      shape={shape}
      size={size}
      width={width}
      height={height}
      bgColor={bgColor}
      waveColor={waveColor}
      index={index}
    />
  );
};
